"use client"

import { useInfiniteQuery, useQuery } from "@tanstack/react-query"

import { useAuth } from "@/hooks/use-auth"
import { getQueryTenantId } from "@/lib/auth/query-tenant-id"
import {
  getDeliveryShipmentAction,
  listDeliveryShipmentsAction,
  type DeliveryShipmentDetail,
  type DeliveryShipmentListItem,
  type DeliveryShipmentListPage,
  type ListDeliveryShipmentsInput,
} from "@/server-actions/delivery-shipments"
import { useTranslations } from "next-intl"


export type DeliveryShipmentStatus = DeliveryShipmentListItem["operationStatus"]

export const deliveryShipmentsQueryPrefix = (tenantId: string) =>
  ["delivery-shipments", tenantId] as const

export const deliveryShipmentsQueryKey = (
  tenantId: string,
  status?: DeliveryShipmentStatus,
) => [
  ...deliveryShipmentsQueryPrefix(tenantId),
  { status: status ?? "all" },
] as const

export const deliveryShipmentDetailQueryKey = (
  tenantId: string,
  shipmentId: string,
) => ["delivery-shipment", tenantId, shipmentId] as const

export function useDeliveryShipmentsQuery(input: {
  status?: DeliveryShipmentStatus
  limit?: number
  enabled?: boolean
} = {}) {
  const { user, isAuthenticated } = useAuth()
  const tenantId = getQueryTenantId(user)

  return useInfiniteQuery({
    queryKey: deliveryShipmentsQueryKey(tenantId, input.status),
    enabled: isAuthenticated && input.enabled !== false,
    initialPageParam: undefined as string | undefined,
    queryFn: async ({ pageParam }): Promise<DeliveryShipmentListPage> => {
      const actionInput: ListDeliveryShipmentsInput = {
        ...(input.status ? { status: input.status } : {}),
        ...(input.limit ? { limit: input.limit } : {}),
        ...(pageParam ? { cursor: pageParam } : {}),
      }
      const result = await listDeliveryShipmentsAction(actionInput)
      if ("status" in result) throw new Error(result.errorMessage)
      return result
    },
    getNextPageParam: lastPage => lastPage.nextCursor ?? undefined,
  })
}

export function useDeliveryShipmentQuery(
  shipmentId: string | null,
  enabled = true,
) {
  const tUi = useTranslations("Feedback.QueriesDeliveryShipments")

  const { user, isAuthenticated } = useAuth()
  const tenantId = getQueryTenantId(user)

  return useQuery<DeliveryShipmentDetail>({
    queryKey: deliveryShipmentDetailQueryKey(tenantId, shipmentId ?? "none"),
    enabled: enabled && isAuthenticated && shipmentId !== null,
    queryFn: async () => {
      if (!shipmentId) throw new Error(tUi("deliveryShipmentIsRequired"))
      const result = await getDeliveryShipmentAction({ shipmentId })
      if ("status" in result) throw new Error(result.errorMessage)
      return result
    },
  })
}
